import React, { useRef } from "react";
import PropTypes from "prop-types";
import TierEditorPanel from "./TierEditorPanel";
import ApprovalChainPanel from "./ApprovalChainPanel";
import Badge from "../../../components/ui-components/Badge";

/**
 * Section header: title + helper text on the left, status chip on the right.
 */
function SectionHeader({ title, helperText, statusChip }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-text">{title}</div>
        <div className="mt-0.5 text-xs text-text-2">{helperText}</div>
      </div>
      <div className="shrink-0 flex items-center">
        <Badge status={statusChip} />
      </div>
    </div>
  );
}

/**
 * TierWorkspacePanel — Single right-side panel combining tier thresholds and approval chain.
 * Re-composes TierEditorPanel + ApprovalChainPanel into one card.
 */
const TierWorkspacePanel = ({
  tier,
  onTierChange,
  onAddTier,
  objectType,
  currency = "EUR",
  disabled,
  errors,
  warnings,
  autoFocusName,
}) => {
  const addStepRef = useRef(null);

  if (!tier) {
    return (
      <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col min-h-0">
        <div className="border-b border-border pb-3 mb-4">
          <h3 className="text-sm font-semibold text-text">Tier workspace</h3>
          <p className="text-xs text-text-2 mt-0.5">Editing thresholds and approval chain for this tier.</p>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 py-8 text-center">
          <p className="text-sm font-medium text-text-2 mb-1">No tier selected</p>
          <p className="text-sm text-text-2 mb-4">Select a tier on the left to edit, or add a tier.</p>
          {!disabled && (
            <button
              type="button"
              onClick={onAddTier}
              className="text-primary hover:text-primary-hover text-sm font-medium cursor-pointer"
            >
              + Add tier
            </button>
          )}
        </div>
      </div>
    );
  }

  const stepsCount = tier?.chain?.length ?? 0;
  const thresholdDefined =
    tier.any_amount === true ||
    (tier.any_amount === false && (tier.min_amount != null || tier.max_amount != null));
  const thresholdStatus = thresholdDefined ? "defined" : "incomplete";
  const chainStatus = stepsCount > 0 ? `${stepsCount} steps` : "no steps";

  return (
    <div className="bg-card border border-card-bd rounded-card p-4 min-w-0 flex flex-col min-h-0">
      <div className="border-b border-border pb-3 mb-4 shrink-0">
        <h3 className="text-sm font-semibold text-text">Tier workspace</h3>
        <p className="text-xs text-text-2 mt-0.5">Editing thresholds and approval chain for this tier.</p>
      </div>

      <div className="flex-1 min-h-0 flex flex-col overflow-hidden space-y-4">
        <section aria-labelledby="tier-details-heading" className="shrink-0">
          <div
            className="rounded-card border border-border bg-surface-2 p-4"
            role="group"
            aria-labelledby="tier-details-heading"
          >
            <SectionHeader
              title="Threshold"
              helperText="Defines when this tier applies."
              statusChip={thresholdStatus}
            />
            <div className="mt-3">
              <TierEditorPanel
                tier={tier}
                onTierChange={onTierChange}
                onAddTier={onAddTier}
                objectType={objectType}
                currency={currency}
                disabled={disabled}
                errors={[]}
                autoFocusName={autoFocusName}
                embedded
              />
            </div>
          </div>
        </section>

        <div className="border-t border-border shrink-0" aria-hidden />

        <section id="approval-chain" aria-labelledby="tier-chain-heading" className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <div
            className="rounded-card border border-border bg-surface-2 p-4 flex-1 min-h-0 flex flex-col overflow-hidden"
            role="group"
            aria-labelledby="tier-chain-heading"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-text">Approval chain</div>
                <div className="mt-0.5 text-xs text-text-2">
                  Approvals required when this tier matches.
                </div>
              </div>
              <div className="shrink-0 flex items-center gap-2">
                <Badge status={chainStatus} />
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => addStepRef.current?.openAddModal?.()}
                    className="text-primary hover:text-primary-hover text-sm font-medium cursor-pointer"
                  >
                    + Add step
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 flex-1 min-h-0 overflow-auto">
              <ApprovalChainPanel
                tier={tier}
                onTierChange={onTierChange}
                disabled={disabled}
                errors={[]}
                warnings={[]}
                embedded
                addStepRef={addStepRef}
              />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

TierWorkspacePanel.propTypes = {
  tier: PropTypes.object,
  onTierChange: PropTypes.func.isRequired,
  onAddTier: PropTypes.func.isRequired,
  objectType: PropTypes.string,
  currency: PropTypes.string,
  disabled: PropTypes.bool,
  errors: PropTypes.array,
  warnings: PropTypes.array,
  autoFocusName: PropTypes.bool,
};

export default TierWorkspacePanel;
